'use client'

import { useEffect, useState } from 'react'
import { priceSourceLabel, STATE_NAMES } from '@/lib/constants'
import type { FuelType } from '@/lib/types'

/**
 * Top-of-page live price ticker.
 *
 * Product intent (set by Chris, 2026-04-18): this is a drop-in reason
 * to visit the site — people checking today's fuel prices nationally
 * or looking for the cheapest/priciest state. No route needed. The
 * ticker is a traffic-return surface, not a progress indicator.
 *
 * Data: shares the same Redis-backed EIA cache that the calculator
 * uses. First paint is effectively instant (cache is warmed on boot
 * by instrumentation.ts), so the ticker lands along with the page.
 *
 * Color semantics mirror the per-state breakdown: green = cheapest,
 * red = priciest, neutral = national average. Lets users carry a
 * single mental model across the whole page.
 */

interface PriceTickerProps {
	fuelType: FuelType
}

interface TickerStats {
	nationalAvg: number
	lowest: { state: string; price: number } | null
	highest: { state: string; price: number } | null
	fetchedAt: number
}

interface FuelPricesApi {
	prices: Record<string, { gas: number; diesel: number }>
	fetchedAt: number
	/**
	 * EIA's real U.S. national average (NUS entry) — properly weighted
	 * across states. Always populated by the server now (Codex Medium
	 * #2 follow-up 2026-04-19); previously the server omitted it on
	 * healthy responses and the ticker computed an unweighted mean of
	 * state entries, which overweighted tiny states.
	 */
	nationalAverage: { gas: number; diesel: number }
}

/**
 * Reduce the full state→price map down to the three stats we display.
 *
 * For "US avg" we surface the response's `nationalAverage` verbatim —
 * that's EIA's real weighted national number. Previous version
 * recomputed an unweighted mean of state entries, which was labelled
 * "US avg" but was not actually the US average. Codex Medium #2
 * follow-up 2026-04-19.
 *
 * Skips the 'US' pseudo-entry when scanning for cheapest/priciest so
 * "cheapest state" doesn't collapse to the national aggregate itself.
 */
function deriveStats(response: FuelPricesApi, fuelType: FuelType): TickerStats {
	let lowest: { state: string; price: number } | null = null
	let highest: { state: string; price: number } | null = null

	for (const [code, entry] of Object.entries(response.prices)) {
		if (code === 'US') continue // EIA nationwide aggregate, not a state
		const price = entry[fuelType]
		if (!Number.isFinite(price) || price <= 0) continue
		if (!lowest || price < lowest.price) lowest = { state: code, price }
		if (!highest || price > highest.price) highest = { state: code, price }
	}

	const nationalAvg = response.nationalAverage[fuelType] ?? 0

	return { nationalAvg, lowest, highest, fetchedAt: response.fetchedAt }
}

export function PriceTicker({ fuelType }: PriceTickerProps) {
	const [stats, setStats] = useState<TickerStats | null>(null)
	const [failed, setFailed] = useState(false)

	useEffect(() => {
		let canceled = false
		// Reset `failed` at the start of every fetch. A transient earlier
		// error (e.g., offline blip on a previous fuelType) must not
		// permanently hide the ticker once we're trying again.
		// Codex Medium #3 follow-up 2026-04-19.
		setFailed(false)
		fetch('/api/fuel-prices')
			.then((r) => (r.ok ? (r.json() as Promise<FuelPricesApi>) : Promise.reject(r.status)))
			.then((data) => {
				if (canceled) return
				setStats(deriveStats(data, fuelType))
				setFailed(false) // explicit clear on success — paranoia / redundant but harmless
			})
			.catch(() => {
				if (!canceled) setFailed(true)
			})
		return () => {
			canceled = true
		}
	}, [fuelType])

	// Silent on failure — this is a nice-to-have, not a blocker. The main
	// calculator has its own warning banner for real fuel-price failures.
	if (failed) return null

	return (
		<section
			aria-label="Live fuel prices"
			className="border-b border-border bg-surface/60 px-3 py-2 sm:px-6"
		>
			<div className="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[11px] sm:flex-nowrap sm:gap-0 sm:text-xs">
				{/* Live indicator — pulse dot + mono caps label */}
				<span className="inline-flex items-center gap-1.5 text-text-muted shrink-0 sm:pr-6 sm:border-r sm:border-border">
					<span className="relative inline-flex size-1.5">
						<span className="absolute inline-flex size-full rounded-full bg-success/60 opacity-75 animate-ping" />
						<span className="relative inline-flex size-1.5 rounded-full bg-success" />
					</span>
					<span className="font-mono uppercase tracking-[0.12em] text-[9px] sm:text-[10px]">
						Live · {fuelType}
					</span>
				</span>

				<div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 sm:flex-nowrap sm:gap-0 sm:divide-x sm:divide-border">
					<Stat
						label="US Avg"
						value={stats ? formatPrice(stats.nationalAvg) : '—'}
						tone="neutral"
					/>

					<Stat
						label="Cheapest"
						value={stats?.lowest ? `${stats.lowest.state} ${formatPrice(stats.lowest.price)}` : '—'}
						suffix={stats?.lowest ? priceSourceLabel(stats.lowest.state) : undefined}
						title={stats?.lowest ? sourceTitle(stats.lowest.state) : undefined}
						tone="good"
					/>

					<Stat
						label="Priciest"
						value={
							stats?.highest ? `${stats.highest.state} ${formatPrice(stats.highest.price)}` : '—'
						}
						suffix={stats?.highest ? priceSourceLabel(stats.highest.state) : undefined}
						title={stats?.highest ? sourceTitle(stats.highest.state) : undefined}
						tone="bad"
					/>
				</div>
			</div>
		</section>
	)
}

/** Format a gallon price: two decimals, no currency abbreviation. */
function formatPrice(price: number): string {
	return `$${price.toFixed(2)}`
}

/**
 * Tooltip text that explains the source tag in plain English. Users who
 * hover can learn why WV and VA might share a price (both sit in PADD R1Z
 * Lower Atlantic), without the label itself being noisy.
 */
function sourceTitle(stateCode: string): string {
	const name = STATE_NAMES[stateCode] ?? stateCode
	return priceSourceLabel(stateCode) === 'state'
		? `${name}: EIA publishes weekly per-state prices for this state.`
		: `${name}: EIA regional PADD average — all states in this PADD share one price.`
}

type Tone = 'good' | 'bad' | 'neutral'

const TONE_CLASS: Record<Tone, string> = {
	good: 'text-success',
	// `text-error-fg` instead of `text-error` — the ticker text is small
	// (11-12px mono), so we need the brighter WCAG-AA-compliant red.
	// See docs/AI_BLIND_SPOTS.md § (Medium #8 Codex follow-up).
	bad: 'text-error-fg',
	neutral: 'text-text-primary',
}

function Stat({
	label,
	value,
	tone,
	title,
	suffix,
}: {
	label: string
	value: string
	tone: Tone
	title?: string
	/** Optional small tag rendered after the value, e.g. "state" or "PADD". */
	suffix?: string
}) {
	return (
		<span
			className="inline-flex items-baseline gap-2 shrink-0 sm:px-6 first:sm:pl-6 last:sm:pr-0"
			title={title}
		>
			<span className="font-mono uppercase tracking-[0.12em] text-[9px] sm:text-[10px] text-text-muted">
				{label}
			</span>
			{/*
			 * min-width reserves space for the value BEFORE it resolves so
			 * the "—" placeholder doesn't shift the layout when it swaps
			 * to a real price. 60px at mono 11px covers "WY $12.75"
			 * (~58px, the widest realistic "XX $NN.NN") — tight enough
			 * that the ticker fits on a 375px mobile viewport without
			 * overflow-scrolling, loose enough to kill CLS. Prior 72px
			 * was over-reserved and pushed the ticker past the viewport.
			 * Codex Medium #9 fix (original) + 2026-04-18 tightening pass.
			 */}
			<span
				className={`font-mono tabular-nums font-medium inline-block min-w-[60px] ${TONE_CLASS[tone]}`}
			>
				{value}
			</span>
			{suffix && (
				// `state`/`PADD` tag hidden on mobile — the information is
				// less actionable on a phone and saves ~60px of horizontal
				// space across the three stats, which is the difference
				// between fitting and scrolling on a 375px viewport.
				<span className="hidden sm:inline font-mono text-[9px] uppercase tracking-[0.12em] text-text-muted">
					{suffix}
				</span>
			)}
		</span>
	)
}
